import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiSend } from "../lib/api.js";
import type { MemberDto } from "./types.js";

/**
 * The backend `PATCH /v1/admin/members/:id/status` endpoint only
 * accepts the operational statuses {active, suspended} — "invited"
 * is owned by the (future) email-verification flow and explicitly
 * rejected by `setMemberStatusBodySchema` in members.dto.ts. Keep
 * this type narrower than MemberStatus so TS forbids passing an
 * invite through the suspend/reactivate mutation.
 */
type SetMemberStatus = "active" | "suspended";

interface SetMemberStatusVars {
  memberId: string;
  status: SetMemberStatus;
}

/**
 * Admin-console mutation: suspend / reactivate a member.
 *
 * Calls the Sprint 7 write endpoint `PATCH /v1/admin/members/:id/status`.
 * On success, invalidate the members list so counts + status chips
 * refresh. Permission gating is done in `MembersPage` via
 * `useAccess().can("tenant.members.write")`; the API enforces the
 * same check — the UI button is hidden to save a round-trip.
 */
export function useSetMemberStatus() {
  const qc = useQueryClient();
  return useMutation<{ member: MemberDto }, Error, SetMemberStatusVars>({
    mutationFn: ({ memberId, status }) =>
      apiSend<{ member: MemberDto }>("PATCH", `/v1/admin/members/${memberId}/status`, {
        status,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "members"] });
    },
  });
}
